// @flow
import './index.less';

import {Skeleton} from 'antd';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';

import PlanItem from '_components/CheckoutSection/PlanItem';
import PromotionCode from '_components/CheckoutSection/PromotionCode';
import {useAuthState} from '_contexts/auth';
import {useCheckout, useCheckoutState} from '_contexts/checkout';
import useAnalytics, {event} from '_hooks/useAnalytics';
import useProducts from '_hooks/useProducts';
import {useQueryParams} from '_hooks/useQueryParams';
import {getPromotionCode} from '_utils/stripe';
import {checkHasExpiredSubscription} from '_utils/subscription';

type Props = {
  active?: boolean,
};

const PlanSelector = ({active = false}: Props): React$Node => {
  const {track} = useAnalytics();

  const {currentUser} = useAuthState();

  const {products, loading} = useProducts();
  const {selectedPlan, promotionCode} = useCheckoutState();
  const {selectPlan, resetPlan} = useCheckout();
  const promoCode = useQueryParams('code');
  const [promoCodeData, setPromoCodeData] = useState();

  const product = useMemo(() => (products && products.length > 0 ? products[0] : null), [products]);

  useEffect(() => {
    (async () => {
      if (!product || !promoCode) return;
      // Pre-select plan based on planType of promotion code (if have)

      try {
        const promotionCodeData = await getPromotionCode(promoCode);
        setPromoCodeData(promotionCodeData);
        const price = product.prices
          .filter(price => price.active)
          .find(p => p.metadata.planType === promotionCodeData?.metadata?.planType);

        if (!price) return;

        selectPlan(price);
        track({
          name: event.af.addToCart,
          data: {
            af_price: price.unitAmount / 100,
            af_content_id: price.id,
            af_content: price.metadata.planType,
            af_currency: price.currency,
          },
        });
      } catch (err) {
        console.log('invalid promo code provide in query param', err);
      }
    })();
  }, [product, promoCode, selectPlan, track]);

  const planItemsElem = useMemo(() => {
    if (!product?.prices) return null;

    return (
      <div className="plan-items">
        {product.prices
          .filter(price => price.active)
          .map((price, idx) => (
            <PlanItem
              key={`plan-item-${idx}`}
              plan={price}
              onClick={() => {
                selectPlan(price);
                track({
                  name: event.af.addToCart,
                  data: {
                    af_price: price.unitAmount / 100,
                    af_content_id: price.id,
                    af_content: price.metadata.planType,
                    af_currency: price.currency,
                  },
                });
              }}
            />
          ))}
      </div>
    );
  }, [product, selectPlan, track]);

  const showDisclaimer = useMemo(() => !checkHasExpiredSubscription(currentUser), [currentUser]);

  const isCodeApplicable = useMemo(
    () => selectedPlan?.metadata?.planType === promoCodeData?.metadata?.planType || !promoCodeData?.metadata?.planType,
    [selectedPlan, promoCodeData],
  );

  if (loading) {
    return (
      <div className={classNames('plan-selector', {'plan-selector-active': active})}>
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className={classNames('plan-selector', {'plan-selector-active': active})}>
      {selectedPlan ? (
        <PlanItem
          plan={selectedPlan}
          promotionCode={promotionCode}
          showDisclaimer={showDisclaimer}
          onClick={resetPlan}
        />
      ) : (
        planItemsElem
      )}
      {selectedPlan && (
        <PromotionCode type="checkout" disabled={loading} code={isCodeApplicable ? promoCode : undefined} />
      )}
    </div>
  );
};

export default PlanSelector;
